<template>
  <div class="pdf-thumbnail-sidebar" ref="sidebar">
    <div
      v-for="page in totalPages"
      :key="page"
      class="pdf-thumbnail-sidebar__item"
      :class="{ active: page === currentPage }"
      :ref="`thumb-${page}`"
      :data-page="page"
      @click="$emit('page-click', page)"
    >
      <canvas :ref="`canvas-${page}`" class="pdf-thumbnail-sidebar__canvas"></canvas>
      <span class="pdf-thumbnail-sidebar__page-number">{{ page }}</span>
    </div>
  </div>
</template>

<script>
const THUMBNAIL_WIDTH = 140;

export default {
  name: 'PDFThumbnailSidebar',
  props: {
    pdfDocument: {
      type: Object,
      required: true,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      totalPages: 0,
      renderedPages: new Set(),
      observer: null,
    };
  },
  methods: {
    async renderThumbnail(pageNum) {
      if (this.renderedPages.has(pageNum)) return;
      if (!this.pdfDocument) return;

      const canvasRefs = this.$refs[`canvas-${pageNum}`];
      const canvas = canvasRefs && canvasRefs[0];
      if (!canvas) return;

      this.renderedPages.add(pageNum);

      try {
        const page = await this.pdfDocument.getPage(pageNum);
        const unscaledViewport = page.getViewport({ scale: 1 });
        const scale = THUMBNAIL_WIDTH / unscaledViewport.width;
        const viewport = page.getViewport({ scale });

        canvas.width = viewport.width;
        canvas.height = viewport.height;

        const ctx = canvas.getContext('2d');
        await page.render({ canvasContext: ctx, viewport }).promise;
      } catch (err) {
        this.renderedPages.delete(pageNum);
      }
    },

    setupObserver() {
      if (this.observer) {
        this.observer.disconnect();
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const pageNum = this.getPageNumFromElement(entry.target);
              if (pageNum) {
                this.renderThumbnail(pageNum);
              }
            }
          });
        },
        {
          root: this.$refs.sidebar,
          rootMargin: '200px 0px',
        },
      );

      this.$nextTick(() => {
        for (let i = 1; i <= this.totalPages; i++) {
          const refs = this.$refs[`thumb-${i}`];
          const el = refs && refs[0];
          if (el) {
            this.observer.observe(el);
          }
        }
      });
    },

    getPageNumFromElement(el) {
      const page = el.dataset.page;
      return page ? parseInt(page, 10) : null;
    },

    async scrollToActiveThumbnail() {
      await this.renderThumbnail(this.currentPage);

      this.$nextTick(() => {
        const refs = this.$refs[`thumb-${this.currentPage}`];
        const el = refs && refs[0];
        if (el) {
          el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      });
    },

    initThumbnails() {
      if (!this.pdfDocument) return;
      this.totalPages = this.pdfDocument.numPages;
      this.renderedPages.clear();

      this.$nextTick(() => {
        this.setupObserver();
      });
    },
  },

  mounted() {
    this.initThumbnails();
  },

  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  },

  watch: {
    pdfDocument() {
      this.initThumbnails();
    },
    currentPage() {
      this.scrollToActiveThumbnail();
    },
  },
};
</script>

<style lang="scss">
.pdf-thumbnail-sidebar {
  flex: 0 0 180px;
  background: $color-white;
  border-right: 1px solid $color-border;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 12px 0;
  user-select: none;

  &::-webkit-scrollbar-track {
    background-color: $color-border;
  }

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: $border-radius-large;
    background-color: $color-normal-pale;
  }

  &__item {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 8px 16px;
    cursor: pointer;
    transition: background-color 150ms ease;

    &:hover {
      background-color: $color-accent-100;
    }

    &.active {
      .pdf-thumbnail-sidebar__canvas {
        box-shadow: 0 0 0 6px $color-border;
      }

      .pdf-thumbnail-sidebar__page-number {
        background: $color-accent;
        color: $color-white;
        font-weight: 600;
        padding: 2px 4px;
        border-radius: $border-radius-small;
      }
    }
  }

  &__canvas {
    max-width: 140px;
    width: 140px;
    min-height: 180px;
    height: auto;
    border: 1px solid $color-border;
    border-radius: 3px;
    background: $color-white;
    transition: box-shadow 150ms ease;
  }

  &__page-number {
    margin-top: 8px;
    font-size: 11px;
    color: $color-normal;
    user-select: none;
    transition: background 150ms ease, color 150ms ease;
  }
}
</style>
